package com.writerslab

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable


// ── Enums for closed sets returned by the analyser API ───────────────
// JSON wire format uses snake_case strings; @SerialName maps them to
// idiomatic Kotlin UPPER_SNAKE constants.

@Serializable
enum class Language {
    @SerialName("ru")    RU,
    @SerialName("eng")   ENG,
    @SerialName("other") OTHER,
}

@Serializable
enum class POV {
    @SerialName("first")   FIRST,
    @SerialName("third")   THIRD,
    @SerialName("mixed")   MIXED,
    @SerialName("unknown") UNKNOWN,
}

@Serializable
enum class Mood {
    @SerialName("bright")        BRIGHT,
    @SerialName("rather_bright") RATHER_BRIGHT,
    @SerialName("neutral")       NEUTRAL,
    @SerialName("rather_dark")   RATHER_DARK,
    @SerialName("dark")          DARK,
    @SerialName("heavy")         HEAVY,
}

@Serializable
enum class Emotion {
    @SerialName("anger")        ANGER,
    @SerialName("anticipation") ANTICIPATION,
    @SerialName("disgust")      DISGUST,
    @SerialName("fear")         FEAR,
    @SerialName("joy")          JOY,
    @SerialName("sadness")      SADNESS,
    @SerialName("surprise")     SURPRISE,
    @SerialName("trust")        TRUST,
}

@Serializable
enum class Tag {
    @SerialName("antiquity") ANTIQUITY,
    @SerialName("medieval") MEDIEVAL,
    @SerialName("19th_century") C19TH_CENTURY,
    @SerialName("ussr") USSR,
    @SerialName("ww2") WW2,
    @SerialName("future") FUTURE,
    @SerialName("school_academy") SCHOOL_ACADEMY,
    @SerialName("sea_ship") SEA_SHIP,
    @SerialName("space") SPACE,
    @SerialName("prison") PRISON,
    @SerialName("castle_palace") CASTLE_PALACE,
    @SerialName("post_apocalypse") POST_APOCALYPSE,
    @SerialName("dystopia") DYSTOPIA,
    @SerialName("cyberpunk") CYBERPUNK,
    @SerialName("steampunk") STEAMPUNK,
    @SerialName("war") WAR,
    @SerialName("investigation") INVESTIGATION,
    @SerialName("magic") MAGIC,
    @SerialName("romance") ROMANCE,
    @SerialName("politics_intrigue") POLITICS_INTRIGUE,
    @SerialName("sport") SPORT,
    @SerialName("heist") HEIST,
    @SerialName("vampires") VAMPIRES,
    @SerialName("werewolves") WEREWOLVES,
    @SerialName("dragons") DRAGONS,
    @SerialName("elves") ELVES,
    @SerialName("zombies") ZOMBIES,
    @SerialName("demons") DEMONS,
    @SerialName("isekai") ISEKAI,
    @SerialName("litrpg") LITRPG,
    @SerialName("academy") ACADEMY,
    @SerialName("apocalypse") APOCALYPSE,
    @SerialName("chosen_one") CHOSEN_ONE,
    @SerialName("dark_lord") DARK_LORD,
    @SerialName("tournament") TOURNAMENT,
    @SerialName("revenge") REVENGE,
    @SerialName("mercenary") MERCENARY,
    @SerialName("ascension") ASCENSION,
    @SerialName("isekai_female") ISEKAI_FEMALE,
    @SerialName("enemies_to_lovers") ENEMIES_TO_LOVERS,
    @SerialName("fake_marriage") FAKE_MARRIAGE,
    @SerialName("forbidden_love") FORBIDDEN_LOVE,
    @SerialName("second_chance") SECOND_CHANCE,
    @SerialName("detective") DETECTIVE,
    @SerialName("thriller") THRILLER,
    @SerialName("sci_fi") SCI_FI,
    @SerialName("fantasy") FANTASY,
    @SerialName("action") ACTION,
    @SerialName("spy") SPY,
    @SerialName("coming_of_age") COMING_OF_AGE,
    @SerialName("redemption") REDEMPTION,
    @SerialName("loss_grief") LOSS_GRIEF,
    @SerialName("power_tyranny") POWER_TYRANNY,
    @SerialName("loneliness") LONELINESS,
    @SerialName("cozy") COZY,
    @SerialName("dark") DARK,
    @SerialName("ironic") IRONIC,
    @SerialName("nostalgic") NOSTALGIC,
}


// ── API DTOs ─────────────────────────────────────────────────────────

@Serializable
data class ApiErrorResponse(
    val error: String? = null
)

@Serializable
data class ApiAnalysisResponse(
    val words: Int = 0,
    val chars: Int = 0,
    val quality: Int? = null,
    val language: Language? = null,
    val pov: POV? = null,
    val characters: Int = 0,
    val tags: List<Tag> = emptyList(),
    val book_id: String = "",
    /** Anonymous read-capability token for this book (present only on anon
     *  uploads). Lets the reader fetch the text/highlights without a login;
     *  stored per-book and sent as the `X-Book-Token` header. */
    val anon_read_token: String = "",
    val profile: ApiMetricGroup = ApiMetricGroup(),
    val character: ApiMetricGroup = ApiMetricGroup(),
    val style: ApiMetricGroup = ApiMetricGroup(),
    /** Population-mean reference per metric, on the same 1-10 scale as
     *  the values in profile / character / style. Only present for metrics
     *  that appear in the language's quality formula. Keys serialise via
     *  `Metric.@SerialName`, so e.g. `LIVELINESS` shows up as
     *  `"bureaucratese"` on the wire. */
    val references: Map<Metric, Double> = emptyMap(),
    /** Signed contribution of each metric to the overall quality z-score
     *  (positive = boost, negative = drag). Pre-computed on the back-end
     *  so the front-end doesn't have to redo z-scoring against raw units
     *  for the EN-extras (whose API values are post-normalised). */
    @SerialName("quality_contributions")
    val qualityContributions: Map<Metric, Double> = emptyMap(),
    /** Per-metric "good direction" derived from the LR coefficients —
     *  which way moves the metric toward predicting `is_popular = 1`.
     *  Values: `"high"` / `"low"` / `"none"`. Used by the front-end to
     *  colour metric bars (green/red/violet) and to decide which cards
     *  are eligible for drag-flagging. Per-language: RU and EN have
     *  different mass-market preferences, so directions differ. */
    @SerialName("quality_directions")
    val qualityDirections: Map<Metric, String> = emptyMap(),
    val mood: ApiMood = ApiMood(),
    /** When the text was last edited in the reader (ISO); null while it is
     *  the uploaded one. Added by the backend. */
    @SerialName("text_edited_at") val textEditedAt: String? = null,
)

// All metric scores are pre-normalised to integers 1-10 by the Python
// analyser (scale()/_normalize_style()/_en_score round and clamp to 1-10).
@Serializable
data class ApiMetricGroup(
    val complexity: Int = 0,
    val vocabulary: Int = 0,
    val pace: Int = 0,
    val dialogue: Int = 0,
    val expressiveness: Int = 0,
    val coherence: Int = 0,
    @SerialName("arc_volatility") val arcVolatility: Int = 0,
    val vividness: Int = 0,
    val pathos: Int = 0,
    val morality: Int = 0,
    @SerialName("author_stance") val authorStance: Int = 0,
    val voices: Int = 0,
    val fillers: Int = 0,
    val sensory: Int = 0,
    val cliches: Int = 0,
    // Nullable: RU dropped the `began` detector, so the analyser omits this
    // key there — `null` means "metric doesn't exist for this language".
    @SerialName("indirect_actions") val indirectActions: Int? = null,
    @SerialName("weak_verbs") val weakVerbs: Int = 0,
    val intensifiers: Int = 0,
    val bureaucratese: Int = 0,
    @SerialName("sentence_variety") val sentenceVariety: Int = 0,

    // English-only extensions (only present when language == ENG).
    @SerialName("syntactic_simplicity") val syntacticSimplicity: Int = 0,
    @SerialName("vocabulary_range")     val vocabularyRange:     Int = 0,
    @SerialName("rare_vocabulary")      val rareVocabulary:      Int = 0,
    @SerialName("register_shift")       val registerShift:       Int = 0,
    @SerialName("vocabulary_age")       val vocabularyAge:       Int = 0,
    @SerialName("stage_directions")     val stageDirections:     Int = 0,
    @SerialName("no_repetition")        val noRepetition:        Int = 0,
    @SerialName("direct_pov")           val directPov:           Int = 0,
    @SerialName("phrasal_verbs")        val phrasalVerbs:        Int = 0,
    @SerialName("no_nominalisation")    val noNominalisation:    Int = 0,
    val concreteness:                   Int = 0,
)

@Serializable
data class ApiMood(
    val id: Mood? = null,
    val emotions: Map<Emotion, Double> = emptyMap(),
)


fun ApiAnalysisResponse.toAnalysisResult(): AnalysisResult {
    val isEnglish = language == Language.ENG
    fun item(id: Metric, value: Int) =
        MetricItem(id, value, references[id], qualityContributions[id])

    val profileItems = mutableListOf(
        item(Metric.COMPLEXITY,     profile.complexity),
        item(Metric.VOCABULARY,     profile.vocabulary),
        item(Metric.PACE,           profile.pace),
        item(Metric.DIALOGUE,       profile.dialogue),
        item(Metric.EXPRESSIVENESS, profile.expressiveness),
        item(Metric.COHERENCE,      profile.coherence),
    )
    if (isEnglish) {
        profileItems += item(Metric.CLEAN_SYNTAX, profile.syntacticSimplicity)
        profileItems += item(Metric.CEFR_RANGE,   profile.vocabularyRange)
    }

    val characterItems = mutableListOf(
        item(Metric.ARC_VOLATILITY, character.arcVolatility),
        item(Metric.VIVIDNESS,      character.vividness),
        item(Metric.PATHOS,         character.pathos),
        item(Metric.MORALITY,       character.morality),
        item(Metric.AUTHOR_STANCE,  character.authorStance),
        item(Metric.VOICES,         character.voices),
    )
    if (isEnglish) {
        characterItems += item(Metric.RARE_VOCABULARY,  character.rareVocabulary)
        characterItems += item(Metric.REGISTER_SHIFT,   character.registerShift)
        characterItems += item(Metric.VOCABULARY_AGE,   character.vocabularyAge)
        characterItems += item(Metric.STAGE_DIRECTIONS, character.stageDirections)
    }

    val styleItems = mutableListOf(
        item(Metric.FILLERS,       style.fillers),
        item(Metric.SENSORY,       style.sensory),
        item(Metric.ORIGINALITY,   style.cliches),
    )
    // DIRECTNESS (indirect_actions) is language-scoped: absent from the response
    // for RU, so add it only when the analyser actually provided it.
    style.indirectActions?.let { styleItems += item(Metric.DIRECTNESS, it) }
    styleItems += listOf(
        item(Metric.VERB_STRENGTH, style.weakVerbs),
        item(Metric.PRECISION,     style.intensifiers),
        item(Metric.LIVELINESS,    style.bureaucratese),
        item(Metric.RHYTHM,        style.sentenceVariety),
    )
    if (isEnglish) {
        styleItems += item(Metric.NO_REPETITION,     style.noRepetition)
        styleItems += item(Metric.DIRECT_POV,        style.directPov)
        styleItems += item(Metric.PHRASAL_VERBS,     style.phrasalVerbs)
        styleItems += item(Metric.NO_NOMINALISATION, style.noNominalisation)
        styleItems += item(Metric.CONCRETENESS,      style.concreteness)
    }

    return AnalysisResult(
        words = words,
        chars = chars,
        quality = quality,
        language = language,
        pov = pov,
        charactersCount = characters,
        mood = mood.id,
        emotionPcts = mood.emotions,
        tags = tags,
        profile = profileItems,
        character = characterItems,
        style = styleItems,
        styleReliable = words >= 30000,
        metricsReliable = words >= 5000,
        qualityContributions = qualityContributions,
        qualityDirections = qualityDirections,
        textEditedAt = textEditedAt,
    )
}
